// TSL (WebGPU) port of Splat.ts's GLSL SplatMaterial — WebGPU migration
// Stage A (see WEBGPU-TSL-PORT-PLAN.md at repo root).
//
// A line-for-line port, deliberately: same textures (centerAndScale
// RGBA32F, covAndColor RGBA32UI), same instanced `splatIndex` attribute fed
// by the CPU sort worker, same early off-screen cull BEFORE the covariance
// math, same covClamp / eigen-decomposition / 2.0·sqrt(λ) quad extent, same
// blending. Differences from the GLSL are only what WebGPU forces:
//   - no textureSize() needed: the texture width is passed as a uniform
//   - clip-space z is [0, w] under WebGPU (vs [-w, w]); the cull keeps the
//     GL test, which is simply more lenient on the near side — the
//     rasterizer clips the rest
//   - output colour-space conversion is done by the node pipeline (the GLSL
//     path did it via <colorspace_fragment>)
//
// Spherical harmonics (degree 1-3) are evaluated exactly like the GLSL:
// same fp16 SH texture rows, same view-direction flip, sRGB → linear after.

import { CustomBlending, NodeMaterial, OneFactor, Vector2 } from 'three/webgpu';
import {
  Discard, Fn, If, attribute, cameraProjectionMatrix, dot, exp, float,
  cameraPosition, clamp, highpModelViewMatrix, instanceIndex, int, ivec2,
  length, log, mat3, max, min, mix, modelWorldMatrix, normalize, packUnorm4x8, positionGeometry,
  pow, sRGBTransferOETF, sqrt, step, textureLoad, transpose, uint, uniform, unpackUnorm4x8,
  varyingProperty, vec2, vec3, vec4,
} from 'three/tsl';

// SH basis constants (match 3DGS eval_sh.py / the GLSL).
const SH_C0 = 0.28209479177387814;
const SH_C1 = 0.4886025119029199;
const SH_C2 = [1.0925484305920792, -1.0925484305920792, 0.31539156525252005, -1.0925484305920792, 0.5462742152960396];
const SH_C3 = [-0.5900435899266435, 2.890611442640554, -0.4570457994644658, 0.3731763325901154, -0.4570457994644658, 1.445305721320277, -0.5900435899266435];

// SH terms per degree (GLSL evalSH / evalSH2 and the inline degree-1
// formula), added up incrementally so the evaluated degree can change live.
// Rows are vec4 nodes: r0 = [dc, f0, f1, f2], r1 = [f3..f6], r2 = [f7..f10],
// r3 = [f11..f14].
const shDeg1 = (r0, { x, y, z }) =>
  r0.y.mul(y).negate().add(r0.z.mul(z)).sub(r0.w.mul(x)).mul(SH_C1);
function shDeg2(r1, r2, { x, y, z }) {
  const xx = x.mul(x), yy = y.mul(y), zz = z.mul(z);
  return x.mul(y).mul(r1.x).mul(SH_C2[0])
    .add(y.mul(z).mul(r1.y).mul(SH_C2[1]))
    .add(zz.mul(2).sub(xx).sub(yy).mul(r1.z).mul(SH_C2[2]))
    .add(x.mul(z).mul(r1.w).mul(SH_C2[3]))
    .add(xx.sub(yy).mul(r2.x).mul(SH_C2[4]));
}
function shDeg3(r2, r3, { x, y, z }) {
  const xx = x.mul(x), yy = y.mul(y), zz = z.mul(z);
  return y.mul(xx.mul(3).sub(yy)).mul(r2.y).mul(SH_C3[0])
    .add(x.mul(y).mul(z).mul(r2.z).mul(SH_C3[1]))
    .add(y.mul(zz.mul(4).sub(xx).sub(yy)).mul(r2.w).mul(SH_C3[2]))
    .add(z.mul(zz.mul(2).sub(xx.mul(3)).sub(yy.mul(3))).mul(r3.x).mul(SH_C3[3]))
    .add(x.mul(zz.mul(4).sub(xx).sub(yy)).mul(r3.y).mul(SH_C3[4]))
    .add(z.mul(xx.sub(yy)).mul(r3.z).mul(SH_C3[5]))
    .add(x.mul(xx.sub(yy.mul(3))).mul(r3.w).mul(SH_C3[6]));
}

// Sign-extending unpack of two int16s from one uint32, matching the GLSL
// unpackInt16: low half → x, high half → y.
const unpackInt16 = (u) => {
  const v = int(u).toVar();
  return vec2(float(v.shiftLeft(16).shiftRight(16)), float(v.shiftRight(16)));
};

// order: optional storage node of sorted splat indices (splatGpuSort.js);
// without it the instanced `splatIndex` attribute (CPU sort worker) is used.
// srgbOutput: write sRGB-encoded colour so blending happens in sRGB space
// like WebGL — requires renderer.outputColorSpace = LinearSRGBColorSpace
// (otherwise WebGPURenderer blends into a LINEAR intermediate buffer, which
// makes faint splats read stronger: visibly hazier, more floaters).
// vertexSrgb (with srgbOutput): the sRGB encoding runs once per splat in the
// vertex shader instead of for every pixel the splat covers — a splat's
// colour is constant over its quad, so the picture is the same; with SH the
// sRGB → linear → sRGB round trip goes away entirely. Same as the GLSL
// path's VS_SRGB (?vsrgb=0 = per pixel, for comparison).
// fragCut: drop a pixel the splat adds less than 1/255 to (invisible in an
// 8-bit image) instead of only past the quad's edge — the corners of a
// shrunk, semi-transparent splat's quad no longer go through blending. Same
// as the GLSL path's FRAG_CUT (?fragCut=0 = edge only).
// packColor: the colour + opacity reach the pixel shader as ONE uint
// (RGBA 8-bit, flat — constant per splat) instead of four interpolated
// floats: a third less per-vertex data to store and fetch per pixel, which
// is what tile-based GPUs (Apple's) pay for — the GLSL path's mediump
// varyings, in WebGPU terms. 8 bits per channel is the output precision
// anyway. ?packColor=0 = four floats.
export function createSplatNodeMaterial({ centerAndScaleTexture, covAndColorTexture, textureWidth, covClamp = 0.1, order = null, srgbOutput = false, shTexture = null, shDegree = 0, opacityExtent = true, vertexSrgb = false, fragCut = false, packColor = false }) {
  const srgbInVertex = srgbOutput && vertexSrgb;
  const viewport = new Vector2(1980, 1080);
  const viewportU = uniform(viewport);
  const focalU = uniform(1);
  const covClampU = uniform(covClamp);
  const densityU = uniform(1); // live splat-count reduction — see the GLSL's density
  // Evaluated SH degree, live (0 = base colour), at most the texture's
  // layout degree `shDegree` (fixed at load) — see the GLSL's shDegree.
  const shEvalU = uniform(shDegree, 'int');
  const widthU = uniform(textureWidth, 'uint');

  // Per-splat colour, either as four floats or packed into one flat uint.
  const vColor = packColor ? varyingProperty('uint', 'vSplatColorPacked') : varyingProperty('vec4', 'vSplatColor');
  const setColor = (c) => vColor.assign(packColor ? packUnorm4x8(c) : c);
  const vPosition = varyingProperty('vec2', 'vSplatPosition');

  const vertexNode = Fn(() => {
    const splatIndex = order ? order.element(instanceIndex) : attribute('splatIndex', 'uint');
    const texPos = ivec2(int(splatIndex.mod(widthU)), int(splatIndex.div(widthU))).toVar('texPos');

    const centerAndScale = textureLoad(centerAndScaleTexture, texPos).toVar('centerAndScale');
    const camspace = highpModelViewMatrix.mul(vec4(centerAndScale.xyz, 1)).toVar('camspace');
    const pos2d = cameraProjectionMatrix.mul(camspace).toVar('pos2d');

    const out = vec4(0, 0, 2, 1).toVar('splatClip');
    vColor.assign(packColor ? uint(0) : vec4(0));
    vPosition.assign(vec2(0));

    // Same fixed per-splat hash as the GLSL: drawn only while below density.
    const h = splatIndex.mul(uint(2654435761)).toVar('densityHash');
    h.assign(h.bitXor(h.shiftRight(uint(16))));
    const kept = float(h.bitAnd(uint(0xFFFFFF))).div(16777216.0).lessThan(densityU);

    const bounds = pos2d.w.mul(1.2);
    If(kept.and(pos2d.z.greaterThanEqual(pos2d.w.negate()))
      .and(pos2d.x.greaterThanEqual(bounds.negate())).and(pos2d.x.lessThanEqual(bounds))
      .and(pos2d.y.greaterThanEqual(bounds.negate())).and(pos2d.y.lessThanEqual(bounds)), () => {
      const covAndColor = textureLoad(covAndColorTexture, texPos).toVar('covAndColor');
      const scale = centerAndScale.w;
      const m11m12 = unpackInt16(covAndColor.x).mul(scale);
      const m13m22 = unpackInt16(covAndColor.y).mul(scale);
      const m23m33 = unpackInt16(covAndColor.z).mul(scale);
      const Vrk = mat3(
        m11m12.x, m11m12.y, m13m22.x,
        m11m12.y, m13m22.y, m23m33.x,
        m13m22.x, m23m33.x, m23m33.y,
      );

      const z = camspace.z;
      const z2 = z.mul(z);
      const J = mat3(
        focalU.div(z), 0, focalU.mul(camspace.x).div(z2).negate(),
        0, focalU.div(z), focalU.mul(camspace.y).div(z2).negate(),
        0, 0, 0,
      );
      const mv = highpModelViewMatrix;
      const W = transpose(mat3(mv[0].xyz, mv[1].xyz, mv[2].xyz));
      const T = W.mul(J).toVar('T');
      const cov = transpose(T).mul(Vrk).mul(T).toVar('cov2d');

      const vCenter = pos2d.xy.div(pos2d.w);
      const diagonal1 = cov[0].x.add(covClampU);
      const offDiagonal = cov[0].y;
      const diagonal2 = cov[1].y.add(covClampU);
      const mid = diagonal1.add(diagonal2).mul(0.5);
      const radius = length(vec2(diagonal1.sub(diagonal2).div(2), offDiagonal));
      const lambda1 = mid.add(radius);
      const lambda2 = max(mid.sub(radius), 0.1);
      const diagonalVector = normalize(vec2(offDiagonal, lambda1.sub(diagonal1))).toVar('diagonalVector');
      const v1 = diagonalVector.mul(min(sqrt(lambda1.mul(2)), 1024));
      const v2 = vec2(diagonalVector.y, diagonalVector.x.negate()).mul(min(sqrt(lambda2.mul(2)), 1024));

      const colorUint = covAndColor.w;
      const alpha = float(colorUint.shiftRight(uint(24))).div(255);
      if (shTexture && shDegree > 0) {
        // Centres are stored as (x, -y, -z); flip the direction back to 3DGS
        // space for the SH lookup (GLSL: dir = vec3(rd.x, -rd.y, -rd.z)).
        const worldCenter = modelWorldMatrix.mul(vec4(centerAndScale.xyz, 1)).xyz;
        const rd = normalize(worldCenter.sub(cameraPosition));
        const dir = vec3(rd.x, rd.y.negate(), rd.z.negate()).toVar('shDir');
        const rowsPerChannel = shDegree === 1 ? 1 : shDegree === 2 ? 3 : 4;
        const rowBase = texPos.y.mul(rowsPerChannel * 3);
        const row = (c, k) => textureLoad(shTexture, ivec2(texPos.x, rowBase.add(c * rowsPerChannel + k))).toVar(`sh${c}_${k}`);
        // Each channel accumulates degree by degree; higher degrees' rows
        // are only read when the live degree needs them.
        const r0 = [0, 1, 2].map((c) => row(c, 0));
        const acc = [0, 1, 2].map((c) => r0[c].x.mul(SH_C0).toVar(`shAcc${c}`));
        If(shEvalU.greaterThanEqual(1), () => {
          [0, 1, 2].forEach((c) => acc[c].addAssign(shDeg1(r0[c], dir)));
        });
        if (shDegree >= 2) {
          If(shEvalU.greaterThanEqual(2), () => {
            [0, 1, 2].forEach((c) => {
              const r1 = row(c, 1), r2 = row(c, 2);
              acc[c].addAssign(shDeg2(r1, r2, dir));
              if (shDegree >= 3) {
                If(shEvalU.greaterThanEqual(3), () => {
                  acc[c].addAssign(shDeg3(r2, row(c, 3), dir));
                });
              }
            });
          });
        }
        // SH evaluates to sRGB: kept as is when the output is sRGB-encoded in
        // the vertex shader, else linearized (proper piecewise) like the GLSL.
        const srgb = clamp(vec3(acc[0].add(0.5), acc[1].add(0.5), acc[2].add(0.5)), 0, 1).toVar('shSrgb');
        if (srgbInVertex) {
          setColor(vec4(srgb, alpha));
        } else {
          const lin = mix(srgb.div(12.92), pow(srgb.add(0.055).div(1.055), vec3(2.4)), step(0.04045, srgb));
          setColor(vec4(lin, alpha));
        }
      } else {
        const rgb = vec3(
          float(colorUint.bitAnd(uint(0xff))).div(255),
          float(colorUint.shiftRight(uint(8)).bitAnd(uint(0xff))).div(255),
          float(colorUint.shiftRight(uint(16)).bitAnd(uint(0xff))).div(255),
        );
        setColor(vec4(srgbInVertex ? sRGBTransferOETF(rgb) : rgb, alpha));
      }
      // Opacity-aware quad size (fill-rate saving, image-neutral): the
      // fragment alpha is exp(-|p|²)·alpha with |p| ≤ 2 on the ±2 quad, so
      // beyond |p| = sqrt(ln(255·alpha)) a splat adds less than 1/255 —
      // invisible in an 8-bit image. Shrink the quad (and the |p| the
      // fragment sees) to that radius; opaque splats keep the full quad
      // (sqrt(ln 255) ≈ 2.35 > 2). Splats below 1/255 at their peak are
      // never emitted (also dropped by the GPU sort).
      const extent = opacityExtent
        ? min(sqrt(max(log(alpha.mul(255)), 0)).div(2), 1)
        : float(1);
      const p = positionGeometry.xy.mul(extent);
      If(alpha.mul(255).greaterThan(1), () => {
        vPosition.assign(p);
        out.assign(vec4(
          vCenter
            .add(v2.mul(p.x).div(viewportU).mul(2))
            .add(v1.mul(p.y).div(viewportU).mul(2)),
          pos2d.z.div(pos2d.w),
          1,
        ));
      });
    });

    return out;
  })();

  const colorNode = Fn(() => {
    const color = (packColor ? unpackUnorm4x8(vColor) : vColor).toVar('splatColor');
    const A = dot(vPosition, vPosition).negate();
    const B = exp(A).mul(color.a).toVar('splatAlpha');
    if (fragCut) If(B.lessThan(1 / 255), () => { Discard(); });
    else If(A.lessThan(-4), () => { Discard(); });
    const rgb = srgbOutput && !srgbInVertex ? sRGBTransferOETF(color.rgb) : color.rgb;
    return vec4(rgb, B);
  })();

  const material = new NodeMaterial();
  material.vertexNode = vertexNode;
  material.colorNode = colorNode;
  material.transparent = true;
  material.depthTest = true;
  material.depthWrite = false;
  material.blending = CustomBlending;
  material.blendSrcAlpha = OneFactor;
  material.toneMapped = false;
  material.fog = false;
  material.forceSinglePass = true;

  // Same property surface update()/SceneHandler already drive on the GLSL
  // material: material.viewport.{x,y}, material.focal, material.covClamp.
  material.viewport = viewport;
  Object.defineProperty(material, 'focal', {
    get: () => focalU.value,
    set: (v) => { focalU.value = v; },
  });
  Object.defineProperty(material, 'covClamp', {
    get: () => covClampU.value,
    set: (v) => { covClampU.value = v; },
  });
  Object.defineProperty(material, 'shDegree', {
    get: () => shEvalU.value,
    set: (v) => { shEvalU.value = Math.max(0, Math.min(shDegree, v | 0)); },
  });
  material.shLayout = shTexture ? shDegree : 0;
  Object.defineProperty(material, 'density', {
    get: () => densityU.value,
    set: (v) => { densityU.value = v; },
  });
  material.isSplatNodeMaterial = true;
  return material;
}
